import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  reloadAndCheckVerified,
  resendVerification,
  getCurrentIdToken,
  firebaseSignOut,
} from '../lib/firebase';

// Non-dismissable modal shown after email/password signup. Blocks the user
// until they click the verification link in their inbox and then press
// "I have verified, continue". On confirm, we reload the Firebase user,
// re-fetch the ID token, and hand it to the backend which mints the app JWT.
//
// There is intentionally no close button or backdrop-dismiss — verification
// is mandatory per product spec.

const EmailVerificationModal = ({ open, email, displayName, onVerified }) => {
  const { loginWithFirebase } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);

  if (!open) return null;

  const handleConfirm = async () => {
    setChecking(true);
    try {
      const verified = await reloadAndCheckVerified();
      if (!verified) {
        toast.error('Still unverified. Click the link in your inbox, then try again.');
        return;
      }
      const idToken = await getCurrentIdToken();
      if (!idToken) {
        toast.error('Session lost. Please sign up again.');
        return;
      }
      const appUser = await loginWithFirebase(idToken, displayName || '');
      toast.success('Email verified — welcome!');
      if (onVerified) onVerified(appUser);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Verification check failed';
      toast.error(typeof msg === 'string' ? msg : 'Verification check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success('Verification email re-sent. Check your inbox.');
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/too-many-requests') {
        toast.error('Too many requests. Wait a minute and try again.');
      } else {
        toast.error(err?.message || 'Could not resend. Try again in a moment.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4
                 bg-black/70 backdrop-blur-sm"
      data-testid="email-verify-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-verify-title"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-primary/40
                   bg-card p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center space-y-5">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                 className="text-primary">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>

          <h3
            id="email-verify-title"
            className="text-2xl font-heading text-foreground"
            data-testid="email-verify-title"
          >
            Please check your email to verify your account
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed">
            A verification link has been sent to{' '}
            <span className="text-primary font-medium" data-testid="email-verify-address">
              {email}
            </span>
            . Open it from your inbox and click the link, then return here and tap continue.
          </p>

          <div className="pt-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={checking}
              data-testid="email-verify-continue"
              className="w-full rounded-full py-3 px-6
                         bg-primary text-primary-foreground hover:opacity-90
                         disabled:opacity-50 disabled:cursor-not-allowed
                         font-medium tracking-wide transition-opacity"
            >
              {checking ? 'Checking…' : 'I have verified, continue'}
            </button>
          </div>

          <div className="text-xs text-muted-foreground">
            Didn't receive it?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              data-testid="email-verify-resend"
              className="text-primary/90 hover:text-primary underline underline-offset-4
                         disabled:opacity-50"
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
          </div>

          <p className="text-[11px] text-muted-foreground/70 italic pt-2 border-t border-border/50">
            Tip: also check your spam folder.
          </p>
        </div>
      </div>
    </div>
  );
};

// Export a tiny helper that callers can use to sign the Firebase user out if
// the user abandons the signup flow (e.g. closing the browser tab and coming
// back later — we intentionally do NOT expose this in the modal itself per
// product spec: the modal cannot be dismissed).
export const abandonVerification = async () => {
  await firebaseSignOut();
};

export default EmailVerificationModal;
